import Link from "next/link";
import type { TermsSectionBlock } from "@/content/parseConsumerTerms";
import { FaqBrandMark } from "@/components/faq/FaqBrandMark";
import { LegalMarkdownText } from "@/components/terms/LegalMarkdownText";

export interface TermsPageViewProps {
  title: string;
  subtitle: string;
  backLabel: string;
  /** Shown above the body when locale is English (binding text remains Spanish). */
  languageNote: string | null;
  introParagraphs: string[];
  sections: TermsSectionBlock[];
}

/**
 * Terms & conditions: same floating shell as FAQ/Contact; legal body is left-aligned
 * for readability.
 */
export function TermsPageView({
  title,
  subtitle,
  backLabel,
  languageNote,
  introParagraphs,
  sections,
}: TermsPageViewProps) {
  return (
    <div className="gruv-faq">
      <div className="gruv-faq__panel gruv-terms">
        <header className="gruv-faq__header">
          <FaqBrandMark />
          <h1 className="gruv-faq__title">{title}</h1>
          <p className="gruv-faq__subtitle">{subtitle}</p>
        </header>

        <Link href="/events" className="gruv-faq__back">
          <span className="gruv-faq__back-arrow" aria-hidden>
            ←
          </span>
          {backLabel}
        </Link>

        <article className="gruv-terms__doc">
          {languageNote ? (
            <p className="gruv-terms__lang-note" role="note">
              {languageNote}
            </p>
          ) : null}
          {introParagraphs.map((text, i) => (
            <LegalMarkdownText
              key={`intro-${i}`}
              text={text}
              className="gruv-terms__p"
              headingClassName="gruv-terms__doc-title"
            />
          ))}
          {sections.map((section) => (
            <section key={section.id} className="gruv-terms__section" aria-labelledby={section.id}>
              <h2 id={section.id} className="gruv-terms__section-title">
                {section.title}
              </h2>
              {section.paragraphs.map((text, j) => (
                <LegalMarkdownText
                  key={`${section.id}-${j}`}
                  text={text}
                  className="gruv-terms__p"
                  headingClassName="gruv-terms__section-title"
                />
              ))}
            </section>
          ))}
        </article>
      </div>
    </div>
  );
}
